/* eslint-disable @typescript-eslint/no-explicit-any */
import styles from '@/components/HookFormComponents/components/EpSwitch/EpSwitch.module.scss';
import { useTranslationStore } from '@/stores';
import { Key, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  EpInfoCard,
  EpStepReview,
  FieldLine,
  FieldLineDescription,
} from '../../../../../components';
import {
  AnonymizeDate,
  anonymizeName,
} from '../../../../../components/HookFormComponents/Utils/anonymizer';
import { birthCalc } from '../../../../../components/HookFormComponents/Utils/birthCalc';
import { FormatBoolean } from '../../../../../components/HookFormComponents/Utils/formatBoolean';
import {
  EpErrorMessage,
  EpSwitch,
} from '../../../../../components/HookFormComponents/components';
import { useTranslation as t } from '../../../../../hooks/useTranslation';
import { globalizationService } from '../../../../../services/globalizationService';
import { usePathname } from 'next/navigation';

/* eslint-disable indent */

export default function Step5IncidentNotification() {
  const pathname = usePathname();
  const { getValues, setValue, trigger } = useFormContext();
  const responsabilityTerm = useWatch({ name: `responsabilityTerm` });
  const values = getValues();
  const tag = 'PSNotificationConfirmation';
  const { translations } = globalizationService.getTranslationsByTag(tag);
  const { language } = useTranslation().i18n;
  const { translations: global } = useTranslationStore();
  setValue('formUtils.stepNeedReview', '');

  useEffect(() => {
    trigger('responsabilityTerm');
  }, [responsabilityTerm, trigger]);

  // LET IT FOR DEBUG
  // useEffect(() => {
  //   console.log(`values no step5:`, values);
  // }, [values]);

  const isLogged = !pathname.includes('Public');

  const notificationIntubationDateId =
    values?.notificationIntubationDateId &&
    new Date(
      `${values?.notificationIntubationDateId} 12:00`,
    )?.toLocaleDateString(language);

  const patientBirthDateFormatted = values?.patientBirthDate
    ? new Date(`${values?.patientBirthDate}`)?.toLocaleDateString(language)
    : '';

  return (
    <>
      <EpInfoCard
        type="filled"
        title={t(translations, 'Confirm the filled data!')}
      >
        <p>
          {t(
            translations,
            'Before sending the notification, please confirm that all data has been filled in correctly, before sending a notification to the Office of Quality and/or the Patient Safety Center.',
          )}
        </p>
        <p>
          {t(
            translations,
            'If you have entered an email, make sure it is correct to follow up on the notification.',
          )}
        </p>
      </EpInfoCard>
      <EpStepReview
        translations={translations}
        title={`1 - ${t(translations, 'Identification')}`}
        step={1}
      >
        {values?.person?.inputValue === 'true' ? (
          <>
            <FieldLine
              label={t(translations, 'Patient Name')}
              text={
                !isLogged
                  ? anonymizeName(values?.patientName)
                  : values?.patientName
              }
            />
            <FieldLine
              label={t(translations, 'Age')}
              text={
                values?.noId
                  ? birthCalc({
                      birthStr: values?.patientBirthDate,
                      finalDateStr: values?.notificationIntubationDateId,
                    })
                  : values?.patientAge
              }
            />
            <FieldLine
              label={t(translations, 'Service number or identification')}
              text={values?.patientIdentification}
            />
            <FieldLine
              label={t(translations, 'Hospital admission date')}
              text={''}
            />

            {values?.personType?.inputValue === '1' &&
            !values?.noId &&
            !isLogged ? (
              <>
                <FieldLine
                  label={t(translations, 'Birth Date')}
                  text={
                    values?.patientBirthDate
                      ? AnonymizeDate(values?.patientBirthDate)
                      : ''
                  }
                />
              </>
            ) : (
              <>
                <FieldLine
                  label={t(translations, 'Birth Date')}
                  text={patientBirthDateFormatted}
                />
              </>
            )}
            {/* <FieldLine
              label={t(translations, 'Birth Date')}
              text={
                !isLogged
                && (
                    (values?.personType === '1' && values?.noId)
                    || values?.personType !== '1'
                  )
                  ? AnonymizeDate(values?.patientBirthDate) : values?.patientBirthDate?.toLocaleDateString(i18n.language)
              }
            /> */}
            <FieldLine
              label={t(translations, 'Gender')}
              text={values?.genderId?.inputLabel}
            />
          </>
        ) : (
          <FieldLine
            label={t(translations, 'Does this notification refer to a person?')}
            text={values?.person?.inputLabel}
          />
        )}
      </EpStepReview>

      <EpStepReview
        translations={translations}
        title={`2 - ${t(translations, 'When and where did it take place?')}`}
        step={2}
      >
        <FieldLine
          label={t(translations, 'Date of the incident')}
          text={notificationIntubationDateId}
        />
        <FieldLine
          label={t(translations, 'Incident time')}
          text={values?.notificationMoreInfoInput?.inputLabel}
        />
        <FieldLine
          label={t(translations, 'Ocurrence local')}
          text={values?.unitId?.inputLabel}
        />
      </EpStepReview>

      <EpStepReview
        translations={translations}
        title={`3 - ${t(
          translations,
          'How did this incident or adverse event occur?',
        )}`}
        step={3}
      >
        <FieldLine
          block
          label={t(translations, 'In your opinion, what happened?')}
          text={values?.notificationTypeId?.inputLabel}
        />
        <FieldLine
          label={t(translations, 'Category')}
          text={values?.notificationCategoryId?.inputLabel}
        />
        <FieldLine
          label={t(translations, 'Incident')}
          text={values?.notificationIncidentId?.inputLabel}
        />
        <FieldLine
          label={t(translations, 'Option')}
          text={values?.notificationOptionId?.inputLabel}
        />
        {values?.questions?.length && Array.isArray(values?.questions[0])
          ? values?.questions?.map((questionList: any) =>
              questionList.map(
                (
                  question: {
                    answers: any;
                    questionDescription: string;
                    inputValue: string;
                    inputLabel: string;
                  },
                  index: Key,
                ) => (
                  <>
                    {Array.isArray(question.inputValue) ? (
                      <FieldLine
                        key={index}
                        label={question.questionDescription}
                        text={question.answers
                          ?.map((value: any) => {
                            if (
                              question.inputValue.includes(
                                value.answerId.toString(),
                              )
                            ) {
                              return value.answerDescription;
                            }
                            return;
                          })
                          .filter(Boolean)
                          .join(', ')}
                      />
                    ) : (
                      <FieldLine
                        key={index}
                        label={question.questionDescription}
                        text={question?.inputLabel || question?.inputValue}
                      />
                    )}
                  </>
                ),
              ),
            )
          : values?.questions?.map(
              (
                question: {
                  answers: any;
                  questionDescription: string;
                  inputValue: string;
                  inputLabel: string;
                },
                index: Key,
              ) => (
                <>
                  {Array.isArray(question.inputValue) ? (
                    <FieldLine
                      key={index}
                      label={question.questionDescription}
                      text={question.answers
                        ?.map((value: any) => {
                          if (
                            question.inputValue.includes(
                              value.answerId.toString(),
                            )
                          ) {
                            return value.answerDescription;
                          }
                          return;
                        })
                        .filter(Boolean)
                        .join(', ')}
                    />
                  ) : (
                    <FieldLine
                      key={index}
                      label={question.questionDescription}
                      text={question?.inputLabel || question?.inputValue}
                    />
                  )}
                </>
              ),
            )}
        <FieldLineDescription
          block
          label={t(translations, 'More information about the Incident')}
          text={values?.descriptionIncident}
        />
        <FieldLineDescription
          block
          label={t(
            translations,
            'Describe the action taken immediately after the incident',
          )}
          text={values?.descriptionActionAfterIncident}
        />

        <FieldLine block label={t(translations, 'Files')} text={' '} />
        {values?.AttachedFiles &&
          values?.AttachedFiles?.map(
            (file: { FileName: string }, index: Key) => (
              <FieldLine key={index} label={''} text={file?.FileName} />
            ),
          )}
      </EpStepReview>

      <EpStepReview
        translations={translations}
        title={`4 - ${t(translations, 'Who witnessed or was informed?')}`}
        step={3}
      >
        <FieldLine
          block
          label={t(
            translations,
            'Did the patient or family member notice the event?',
          )}
          text={values?.patientOrFamilyKnow?.inputLabel}
        />
        <FieldLine
          block
          label={t(translations, 'Was there a negative manifestation?')}
          text={values?.thereWasNegativeDemonstration?.inputLabel}
        />
        <FieldLine
          block
          label={t(
            translations,
            'Did you witness the signaling of juridical, press or social media activation by the family?',
          )}
          text={
            values?.witnessSignalingLegalCompanySocialMediaByFamily?.inputLabel
          }
        />
        <FieldLine
          block
          label={t(translations, 'Was a hospital employee notified? Who?')}
          text={values?.whoWasNotified}
        />
      </EpStepReview>

      {/* <EpStepReview title={`5 - ${t(translations, 'Attached files in this notification')}`} step={3}>


      </EpStepReview> */}

      <EpStepReview
        translations={translations}
        title={`5 - ${t(translations, 'Detection Mechanism')}`}
        step={4}
      >
        <FieldLine
          block
          label={t(translations, 'Name')}
          text={values?.notifierName}
        />
        <FieldLine
          label={t(translations, 'You are')}
          text={values?.occupationId?.inputLabel}
        />
        <FieldLine
          label={t(translations, 'E-mail')}
          text={values?.notifierEmail}
        />
        <FieldLine
          label={t(translations, 'Notifying Sector')}
          text={values?.notifierUnitId?.inputLabel}
        />
        <FieldLine
          label={t(translations, 'Audit')}
          text={t(
            global['global'],
            FormatBoolean(values?.safetyHuddleOrProactiveAction),
          )}
        />
      </EpStepReview>
      <EpInfoCard title={t(translations, 'Statement of responsibility') + ' *'}>
        <EpSwitch
          switchRequired
          switchName="responsabilityTerm"
          switchLabel={t(
            translations,
            'I declare that all information provided is true, and I assume full responsibility for it.',
          )}
          className={styles['ep-switch--with-margin']}
        />
        <EpErrorMessage inputName={`responsabilityTerm`} />
      </EpInfoCard>
    </>
  );
}
